/**
 * @fileoverview SSE Connection Hook
 * 
 * Manages Server-Sent Events connection for real-time optimization updates.
 * Single responsibility: connect/disconnect and track connection state.
 */

import { useState, useCallback } from 'react';
import { optimizationEventService } from '../../services/optimization/OptimizationEventService';
import type { SSEConnectionControls } from './types';

export interface UseSSEConnectionOptions {
  /** Callback when connection state changes */
  onConnectionChange?: (connected: boolean) => void;
}

export interface UseSSEConnectionReturn extends SSEConnectionControls {
  /** Attempt to reconnect if disconnected */
  reconnect: () => void;
  /** Check current connection status without state update */
  isConnected: () => boolean;
}

/**
 * Hook for managing SSE connection lifecycle
 */
export function useSSEConnection(
  options: UseSSEConnectionOptions = {}
): UseSSEConnectionReturn {
  const { onConnectionChange } = options;
  const [connected, setConnected] = useState(false);

  const updateConnectionState = useCallback((isConnected: boolean) => {
    setConnected(isConnected);
    onConnectionChange?.(isConnected);
  }, [onConnectionChange]);

  const connect = useCallback(() => {
    try {
      if (!optimizationEventService.isConnectedToSSE()) {
        optimizationEventService.connect();
      }
      const isConnected = optimizationEventService.isConnectedToSSE();
      updateConnectionState(isConnected);
    } catch {
      updateConnectionState(false);
    }
  }, [updateConnectionState]);

  const disconnect = useCallback(() => {
    try {
      optimizationEventService.disconnect();
    } catch {
      // Disconnect error, ignore
    }
    updateConnectionState(false);
  }, [updateConnectionState]);

  const reconnect = useCallback(() => {
    disconnect();
    // Small delay before reconnecting
    setTimeout(connect, 100);
  }, [connect, disconnect]);

  const isConnected = useCallback(() => {
    return optimizationEventService.isConnectedToSSE();
  }, []);

  return {
    connected,
    connect,
    disconnect,
    reconnect,
    isConnected,
  };
}
